import { Heading as ChakraHeading } from '@chakra-ui/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { fontSizes, fontStyles, TFontStyle, THeaderFontSize } from '~ui/styleGuide/fontStyles'
import { getSpacingValue, TSpace } from '~ui/styleGuide/spaces'
import { TChakraAsProp, TChakraTypographyProps } from '~ui/typings/chakra'
import { responsiveFontByWidth, TRespFontByWithOptions } from '~ui/utils/responsiveFonts'

import { TUseTextPropsOptions, useTextProps } from '../hooks/useTextProps'

type TChakraProps = TChakraAsProp & TChakraTypographyProps

type TStyledProps = {
  responsiveSize?: TRespFontByWithOptions
}

type TProps = Children &
  TChakraProps &
  TStyledProps &
  TUseTextPropsOptions & {
    /**
     * key of the predefined font styles
     * might set attributes like font family, weight, casing, ...
     */
    fontStyle?: TFontStyle
    /**
     * key of the predefined text font sizes
     */
    fontSize?: THeaderFontSize
    /**
     * space inside the heading box,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    padding?: TSpace
  }

// get rid of the React error when passing unknown prop to the HTML element
const propForwardOptions = {
  shouldForwardProp: (propName: string) => propName !== `responsiveSize`,
}

const StyledHeading = styled(ChakraHeading, propForwardOptions)<TStyledProps>`
  ${({ responsiveSize }) => (responsiveSize ? responsiveFontByWidth(responsiveSize) : '')};
`

/**
 * Basic heading component with automatic color selection (based on the consumed bgColor context & the text `contrast`),
 * and option for text truncating (with ...).
 * Font style (family, weight, ...) and size should be selected.
 *
 * Used via more specific components bellow.
 */
const Heading: React.FC<TProps> = ({
  padding,
  children,
  fontSize,
  responsiveSize,
  contrast = `normal`,
  truncateOnLines = 0,
  fontStyle = `regular`,
  ...chakraProps
}) => {
  const { textColorValue, isTruncated, noOfLines } = useTextProps({
    truncateOnLines,
    contrast,
  })

  if (responsiveSize !== undefined && fontSize !== undefined) {
    // TODO console.warning(`You are using both responsiveSize and fontSize at once in Heading component. Make up your mind (fontSize is now ignored)!`)
  }

  const paddingValue = padding && getSpacingValue(padding)

  return (
    <StyledHeading
      textAlign={`left`}
      {...chakraProps}
      {...fontStyles[fontStyle]}
      noOfLines={noOfLines}
      color={textColorValue}
      padding={paddingValue}
      isTruncated={isTruncated}
      responsiveSize={responsiveSize}
      fontSize={fontSizes.header[fontSize ?? `l20`]}
    >
      {children}
    </StyledHeading>
  )
}

type THProps = Omit<TProps, 'as'>

// use these for specific semantic headings
export const H1: React.FC<THProps> = (props) => <Heading {...props} as={`h1`} />
export const H2: React.FC<THProps> = (props) => <Heading {...props} as={`h2`} />
export const H3: React.FC<THProps> = (props) => <Heading {...props} as={`h3`} />
export const H4: React.FC<THProps> = (props) => <Heading {...props} as={`h4`} />
export const H5: React.FC<THProps> = (props) => <Heading {...props} as={`h5`} />
